class StudioSlider {
    constructor(root, options = {}) {
        this.root = root;
        this.viewport = root.querySelector('[data-studio-slider-viewport]');
        this.track = root.querySelector('[data-studio-slider-track]');
        this.slides = Array.from(root.querySelectorAll('[data-studio-slide]'));
        this.leftHotspot = root.querySelector('[data-studio-hover="left"]');
        this.rightHotspot = root.querySelector('[data-studio-hover="right"]');
        this.leftPreview = root.querySelector('[data-studio-preview="left"]');
        this.rightPreview = root.querySelector('[data-studio-preview="right"]');
        this.leftPreviewVideo = root.querySelector('[data-preview-video="left"]');
        this.rightPreviewVideo = root.querySelector('[data-preview-video="right"]');
        this.progressBar = root.querySelector('[data-studio-progress]');
        this.progressFill = root.querySelector('.studio-slider__progress-fill');
        this.progressTimer = null;
        this.videoProgressRafId = null;
        // Get slide duration from data attribute (in seconds), default to 5 seconds
        const durationAttr = root.dataset.slideDuration;
        this.defaultSlideDuration = (durationAttr ? parseInt(durationAttr, 10) : 5) * 1000;
        this.slideDuration = this.defaultSlideDuration; // Will be overridden for video slides
        this.currentIndex = 0;
        this.hoverSuppressed = false;
        this.hoverResumeListenerAttached = false;
        this.lastPointer = null;
        this.suppressedPointer = null;
        this.suppressedAt = 0;
        this.isPaused = true; // Start paused by default
        this.pausedProgress = null; // Store progress when paused
        this.currentVideo = null; // Track the currently playing video

        this.breakpointMql = null;

        if (!this.viewport || !this.track || this.slides.length === 0) return;

        this.isSingleSlide = this.slides.length === 1;
        if (this.isSingleSlide) {
            this.root.classList.add('is-static');
        }

        this.bindEvents();
        // Only sync visuals but don't start autoplay - wait for play() call
        this.updateActiveStates();
        this.updatePreviews();
        this.initProgressBar();
        this.root.classList.add('is-initialized');

        // Auto-start if specified (for first project)
        if (options.autoStart) {
            this.play();
        }
    }

    isMobileViewport() {
        // Keep in sync with studio-slider.css breakpoint (max-width: 749px)
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
        return window.matchMedia('(max-width: 749px)').matches;
    }

    getMediaVariantContainer(slide, variant) {
        if (!slide) return null;
        return slide.querySelector(`[data-studio-media-variant="${variant}"]`);
    }

    getActiveSlideContainer(slide) {
        const variant = this.isMobileViewport() ? 'mobile' : 'desktop';
        return this.getMediaVariantContainer(slide, variant) || slide;
    }

    getDesktopSlideContainer(slide) {
        return this.getMediaVariantContainer(slide, 'desktop') || slide;
    }

    getActiveSlideVideo(slide) {
        const container = this.getActiveSlideContainer(slide);
        return container?.querySelector('[data-studio-video]') || null;
    }

    getDesktopSlideVideo(slide) {
        const container = this.getDesktopSlideContainer(slide);
        return container?.querySelector('[data-studio-video]') || null;
    }

    getActiveSlideImage(slide) {
        const container = this.getActiveSlideContainer(slide);
        return container?.querySelector('img') || null;
    }

    getDesktopSlideImage(slide) {
        const container = this.getDesktopSlideContainer(slide);
        return container?.querySelector('img') || null;
    }

    ensureSlideHydrated(slide, options = {}) {
        if (!slide) return;

        const {
            variant = null,
            forPlayback = false,
        } = options;

        const activeVariant = this.isMobileViewport() ? 'mobile' : 'desktop';
        const variants = variant ? [variant] : [activeVariant];

        variants.forEach((v) => {
            const container = this.getMediaVariantContainer(slide, v) || slide;
            if (!container) return;

            // Hydrate deferred image markup
            const imageTemplate = container.querySelector('template[data-studio-image]');
            if (imageTemplate && !container.querySelector('img')) {
                const fragment = imageTemplate.content.cloneNode(true);
                const img = fragment.querySelector('img');
                if (img) {
                    // The first visible slide should prioritize loading.
                    if (forPlayback) {
                        try {
                            img.loading = 'eager';
                            img.fetchPriority = 'high';
                        } catch (_e) {
                            // no-op
                        }
                    }
                }
                container.appendChild(fragment);
            }

            // Hydrate deferred video sources
            const video = container.querySelector('[data-studio-video]');
            const sourcesTemplate = container.querySelector('template[data-studio-video-sources]');
            if (video && sourcesTemplate && !video.querySelector('source')) {
                const fragment = sourcesTemplate.content.cloneNode(true);
                video.appendChild(fragment);
                video.preload = forPlayback ? 'auto' : 'metadata';
                try {
                    video.load();
                } catch (_e) {
                    // no-op
                }
            }
        });
    }

    bindEvents() {
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleLeave = this.handleLeave.bind(this);
        this.handleHoverLeft = () => this.setHoverState('left');
        this.handleHoverRight = () => this.setHoverState('right');
        this.handleHoverClear = () => this.setHoverState(null);
        this.handlePointerTrack = (event) => {
            this.lastPointer = { x: event.clientX, y: event.clientY };
        };
        this.handleHoverResume = (event) => this.maybeResumeHoverFromPointer(event);
        this.handleClickLeft = (event) => this.goToPrev(event);
        this.handleClickRight = (event) => this.goToNext(event);

        this.viewport.addEventListener('keydown', this.handleKeydown);
        this.viewport.addEventListener('mouseleave', this.handleLeave);
        this.viewport.addEventListener('mousemove', this.handlePointerTrack, { passive: true });

        // Re-evaluate which media element is active when switching breakpoints.
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
            this.breakpointMql = window.matchMedia('(max-width: 749px)');
            this.handleBreakpointChange = () => {
                // Switching variants may change whether the current slide is a video.
                this.handleVideoPlayback();
                this.updatePreviews();
                if (!this.isPaused) {
                    this.updateProgressBar();
                }
            };

            if (typeof this.breakpointMql.addEventListener === 'function') {
                this.breakpointMql.addEventListener('change', this.handleBreakpointChange);
            } else if (typeof this.breakpointMql.addListener === 'function') {
                this.breakpointMql.addListener(this.handleBreakpointChange);
            }
        }

        if (this.leftHotspot) {
            this.leftHotspot.addEventListener('mouseenter', this.handleHoverLeft);
            this.leftHotspot.addEventListener('mouseleave', this.handleHoverClear);
            this.leftHotspot.addEventListener('click', this.handleClickLeft);
            this.leftHotspot.addEventListener('mouseenter', () => this.playPreviewVideo('left'));
            this.leftHotspot.addEventListener('mouseleave', () => this.stopPreviewVideo('left'));
        }

        if (this.rightHotspot) {
            this.rightHotspot.addEventListener('mouseenter', this.handleHoverRight);
            this.rightHotspot.addEventListener('mouseleave', this.handleHoverClear);
            this.rightHotspot.addEventListener('click', this.handleClickRight);
            this.rightHotspot.addEventListener('mouseenter', () => this.playPreviewVideo('right'));
            this.rightHotspot.addEventListener('mouseleave', () => this.stopPreviewVideo('right'));
        }
    }

    handleKeydown(event) {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goToPrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goToNext();
        }
    }

    handleLeave() {
        this.root.classList.remove('is-hovering-left', 'is-hovering-right');
    }

    setHoverState(side, options = {}) {
        if (this.isSingleSlide) return;
        const { force = false } = options;
        if (this.hoverSuppressed && !force) return;
        this.root.classList.toggle('is-hovering-left', side === 'left');
        this.root.classList.toggle('is-hovering-right', side === 'right');
    }

    goToNext(event) {
        if (this.isSingleSlide) return;

        // Clear any paused progress when manually navigating
        this.pausedProgress = null;

        // Hide preview immediately on navigation; only re-activate after mouse movement.
        this.suppressHoverUntilMove(event);
        this.root.classList.remove('is-hovering-left', 'is-hovering-right', 'is-expanding-left', 'is-expanding-right');
        this.stopPreviewVideo('left');
        this.stopPreviewVideo('right');

        requestAnimationFrame(() => {
            this.currentIndex = (this.currentIndex + 1) % this.slides.length;
            this.sync();
        });
    }

    goToPrev(event) {
        if (this.isSingleSlide) return;

        // Clear any paused progress when manually navigating
        this.pausedProgress = null;

        // Hide preview immediately on navigation; only re-activate after mouse movement.
        this.suppressHoverUntilMove(event);
        this.root.classList.remove('is-hovering-left', 'is-hovering-right', 'is-expanding-left', 'is-expanding-right');
        this.stopPreviewVideo('left');
        this.stopPreviewVideo('right');

        requestAnimationFrame(() => {
            this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
            this.sync();
        });
    }

    suppressHoverUntilMove(event) {
        if (this.isSingleSlide) return;
        this.hoverSuppressed = true;
        this.root.classList.add('is-hover-suppressed');
        this.setHoverState(null, { force: true });

        this.suppressedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

        const x = event?.clientX ?? this.lastPointer?.x ?? null;
        const y = event?.clientY ?? this.lastPointer?.y ?? null;
        this.suppressedPointer = x === null || y === null ? null : { x, y };

        if (this.viewport && !this.hoverResumeListenerAttached) {
            this.hoverResumeListenerAttached = true;
            this.viewport.addEventListener('mousemove', this.handleHoverResume, { passive: true });
        }
    }

    maybeResumeHoverFromPointer(event) {
        if (!this.hoverSuppressed) return;

        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        // Prevent immediate re-enable from tiny pointer jitter during click.
        if (this.suppressedAt && (now - this.suppressedAt) < 180) return;

        // Some browsers emit a mousemove right after click (0–1px delta).
        // Require a small real movement before re-enabling hover previews.
        const thresholdPx = 4;
        if (this.suppressedPointer && event) {
            const dx = (event.clientX ?? this.suppressedPointer.x) - this.suppressedPointer.x;
            const dy = (event.clientY ?? this.suppressedPointer.y) - this.suppressedPointer.y;
            if ((dx * dx) + (dy * dy) < thresholdPx * thresholdPx) return;
        }

        this.detachHoverResumeListener();
        this.resumeHoverFromPointer(event);
    }

    detachHoverResumeListener() {
        if (!this.viewport || !this.hoverResumeListenerAttached) return;
        this.viewport.removeEventListener('mousemove', this.handleHoverResume);
        this.hoverResumeListenerAttached = false;
    }

    resumeHoverFromPointer(event) {
        this.hoverSuppressed = false;
        this.root.classList.remove('is-hover-suppressed');
        this.suppressedPointer = null;
        this.suppressedAt = 0;
        this.applyHoverFromPointer(event);
    }

    applyHoverFromPointer(event) {
        if (!this.viewport || this.isSingleSlide) return;
        const rect = this.viewport.getBoundingClientRect();
        if (!rect || !rect.width) return;

        const x = event?.clientX ?? 0;
        const y = event?.clientY ?? 0;
        const insideX = x >= rect.left && x <= rect.right;
        const insideY = y >= rect.top && y <= rect.bottom;
        if (!insideX || !insideY) return;

        const relativeX = (x - rect.left) / rect.width;
        let side = null;
        if (relativeX <= 0.2) {
            side = 'left';
        } else if (relativeX >= 0.7) {
            side = 'right';
        }

        this.setHoverState(side);
    }

    sync() {
        this.root.style.setProperty('--current-index', this.currentIndex);
        this.updateActiveStates();
        this.handleVideoPlayback();
        this.updatePreviews();
        this.updateProgressBar();
    }

    stopVideoProgressLoop() {
        if (this.videoProgressRafId) {
            cancelAnimationFrame(this.videoProgressRafId);
            this.videoProgressRafId = null;
        }
    }

    startVideoProgressLoop(video) {
        if (!this.progressFill || !video) return;

        this.stopVideoProgressLoop();

        const tick = () => {
            if (this.isPaused) {
                this.videoProgressRafId = null;
                return;
            }

            const currentSlide = this.slides[this.currentIndex];
            const stillCurrent = currentSlide && this.getActiveSlideVideo(currentSlide) === video;
            if (!stillCurrent) {
                this.videoProgressRafId = null;
                return;
            }

            const duration = video.duration;
            const currentTime = video.currentTime;

            this.progressFill.style.transition = 'none';

            if (duration && isFinite(duration) && duration > 0) {
                const ratio = Math.max(0, Math.min(1, currentTime / duration));
                this.progressFill.style.width = `${ratio * 100}%`;
            } else {
                // Metadata not ready yet; keep the bar at 0%.
                this.progressFill.style.width = '0%';
            }

            this.videoProgressRafId = requestAnimationFrame(tick);
        };

        this.videoProgressRafId = requestAnimationFrame(tick);
    }

    handleVideoPlayback() {
        const currentSlide = this.slides[this.currentIndex];
        // Ensure media exists before we attempt to play/inspect video elements.
        this.ensureSlideHydrated(currentSlide, { forPlayback: !this.isPaused });
        const video = this.getActiveSlideVideo(currentSlide);
        const isVideoSlide = !!video;

        // Stop any previous per-frame progress tracking; we'll restart if needed.
        this.stopVideoProgressLoop();

        // Pause/reset all other videos (including the non-active variant in the current slide)
        this.slides.forEach((slide) => {
            slide.querySelectorAll('[data-studio-video]').forEach((v) => {
                if (!v || v === video) return;
                v.pause();
                v.currentTime = 0;
            });
        });

        this.currentVideo = null;

        if (isVideoSlide && video) {
            this.currentVideo = video;

            // Ensure we can advance when the video finishes.
            // (If a loop attribute sneaks in, 'ended' will never fire.)
            video.loop = false;

            // Set slide duration based on video duration
            if (video.readyState >= 1 && video.duration && isFinite(video.duration)) {
                this.slideDuration = video.duration * 1000;
            } else {
                // Wait for metadata to load
                this.slideDuration = this.defaultSlideDuration; // Temporary
                video.addEventListener('loadedmetadata', () => {
                    if (this.slides[this.currentIndex] === currentSlide && video.duration && isFinite(video.duration)) {
                        this.slideDuration = video.duration * 1000;
                        // Restart progress bar with correct duration
                        if (!this.isPaused) {
                            this.updateProgressBar();
                        }
                    }
                }, { once: true });
            }

            // Handle video end - go to next slide
            video.onended = () => {
                if (this.slides[this.currentIndex] === currentSlide && !this.isPaused) {
                    this.goToNext();
                }
            };

            // Play video if slider is not paused
            if (!this.isPaused) {
                video.currentTime = 0;
                video.play().catch(() => { });

                // Keep progress bar in sync with actual playback time.
                this.startVideoProgressLoop(video);
            }
        } else {
            // Reset to default duration for image slides
            this.slideDuration = this.defaultSlideDuration;
        }
    }

    updateActiveStates() {
        this.slides.forEach((slide, index) => {
            const isActive = index === this.currentIndex;
            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
            slide.setAttribute('tabindex', isActive ? '0' : '-1');
        });
    }

    updatePreviews() {
        if (this.isSingleSlide) return;
        const total = this.slides.length;
        const prevIndex = (this.currentIndex - 1 + total) % total;
        const nextIndex = (this.currentIndex + 1) % total;

        // Previews read from the desktop variant; ensure at least that variant is hydrated
        // for the adjacent slides so video previews can copy <source> tags.
        this.ensureSlideHydrated(this.slides[prevIndex], { variant: 'desktop', forPlayback: false });
        this.ensureSlideHydrated(this.slides[nextIndex], { variant: 'desktop', forPlayback: false });

        this.setPreview(this.leftPreview, this.slides[prevIndex], '--preview-image-left', this.leftPreviewVideo);
        this.setPreview(this.rightPreview, this.slides[nextIndex], '--preview-image-right', this.rightPreviewVideo);
    }

    setPreview(target, slide, cssVar, previewVideo) {
        if (!target || !slide) return;

        let source = '';
        const video = this.getDesktopSlideVideo(slide);
        const isVideoSlide = !!video;

        if (isVideoSlide) {
            // For video slides, set up video preview
            const posterSource = slide.dataset.videoPoster || video?.poster || slide.dataset.image || '';

            // Set poster as fallback background
            if (posterSource) {
                target.style.setProperty(cssVar, `url("${posterSource}")`);
            }

            // Set up video preview
            if (previewVideo && video) {
                // Copy video sources to preview video
                const sources = video.querySelectorAll('source');
                previewVideo.innerHTML = '';
                sources.forEach(src => {
                    const newSource = document.createElement('source');
                    newSource.src = src.src;
                    newSource.type = src.type;
                    previewVideo.appendChild(newSource);
                });
                previewVideo.load();
                target.classList.add('has-video');
            }
            target.classList.remove('is-empty');
        } else {
            // For image slides
            const image = this.getDesktopSlideImage(slide);
            source = image?.currentSrc || image?.src || slide.dataset.image || '';

            // Clear video preview for image slides
            if (previewVideo) {
                previewVideo.innerHTML = '';
                previewVideo.removeAttribute('src');
                target.classList.remove('has-video');
            }

            if (source) {
                target.style.setProperty(cssVar, `url("${source}")`);
                target.classList.remove('is-empty');
            } else {
                target.classList.add('is-empty');
                target.style.removeProperty(cssVar);
            }
        }
    }

    playPreviewVideo(side) {
        const previewVideo = side === 'left' ? this.leftPreviewVideo : this.rightPreviewVideo;
        if (previewVideo && previewVideo.querySelector('source')) {
            previewVideo.currentTime = 0;
            previewVideo.play().catch(() => { });
        }
    }

    stopPreviewVideo(side) {
        const previewVideo = side === 'left' ? this.leftPreviewVideo : this.rightPreviewVideo;
        if (previewVideo) {
            previewVideo.pause();
            previewVideo.currentTime = 0;
        }
    }

    initProgressBar() {
        if (!this.progressFill) return;
        // Progress bar represents the current slide only.
        // Start at 0% and animate to 100% per slide.
        this.progressFill.style.transition = 'none';
        this.progressFill.style.width = '0%';
    }

    play() {
        if (!this.isPaused) return;
        this.isPaused = false;
        this.root.classList.add('is-playing');
        this.root.classList.remove('is-paused');

        const currentSlide = this.slides[this.currentIndex];
        // Hydrate the active slide before attempting playback.
        this.ensureSlideHydrated(currentSlide, { forPlayback: true });
        const video = this.getActiveSlideVideo(currentSlide);

        // If we're starting on a video slide (common for the first slide), we must wire
        // up the ended handler here. Otherwise the video can finish without advancing,
        // because handleVideoPlayback() only runs on sync() (navigation).
        if (video) {
            this.currentVideo = video;
            video.loop = false;
            video.onended = () => {
                if (this.slides[this.currentIndex] === currentSlide && !this.isPaused) {
                    this.goToNext();
                }
            };

            if (video.duration && isFinite(video.duration)) {
                this.slideDuration = video.duration * 1000;
            }
        }

        // Resume from paused progress or start fresh
        if (this.pausedProgress !== null) {
            this.resumeProgressBar();
        } else {
            this.updateProgressBar();
        }

        // Start/restart playback after progress is configured.
        if (video) {
            if (this.pausedProgress === null) {
                // Fresh start on this slide.
                video.currentTime = 0;
            }
            video.play().catch(() => { });

            // Progress bar should follow actual video playback.
            this.startVideoProgressLoop(video);
        }
    }

    pause() {
        if (this.isPaused) return;
        this.isPaused = true;
        this.root.classList.remove('is-playing');
        this.root.classList.add('is-paused');

        this.stopVideoProgressLoop();

        // Pause current video if any
        if (this.currentVideo) {
            this.currentVideo.pause();
        }

        // Store current progress and stop timer
        if (this.progressTimer) {
            clearTimeout(this.progressTimer);
            this.progressTimer = null;
        }

        // Calculate and store current progress
        if (this.progressFill) {
            const computedWidth = window.getComputedStyle(this.progressFill).width;
            const parentWidth = this.progressFill.parentElement?.offsetWidth || 1;
            const currentRatio = parseFloat(computedWidth) / parentWidth;

            const activeVideo = this.currentVideo;
            const videoRatio =
                activeVideo && activeVideo.duration && isFinite(activeVideo.duration) && activeVideo.duration > 0
                    ? activeVideo.currentTime / activeVideo.duration
                    : null;

            this.pausedProgress = {
                ratio: videoRatio !== null ? Math.max(0, Math.min(1, videoRatio)) : currentRatio,
                slideIndex: this.currentIndex,
                videoTime: this.currentVideo?.currentTime || null
            };

            // Freeze the progress bar at current position
            this.progressFill.style.transition = 'none';
            this.progressFill.style.width = computedWidth;
        }
    }

    resumeProgressBar() {
        if (!this.progressFill || !this.pausedProgress) return;

        // If we paused on a different slide, reset progress for current slide
        if (this.pausedProgress.slideIndex !== this.currentIndex) {
            this.pausedProgress = null;
            this.updateProgressBar();
            return;
        }

        // Handle video resume - sync video time if available
        const currentSlide = this.slides[this.currentIndex];
        const video = this.getActiveSlideVideo(currentSlide);
        if (video && this.pausedProgress.videoTime !== null) {
            video.currentTime = this.pausedProgress.videoTime;
        }

        // For video slides, resume progress from actual playback time.
        if (video) {
            const currentRatio = Math.max(0, Math.min(1, this.pausedProgress.ratio));
            this.progressFill.style.transition = 'none';
            this.progressFill.style.width = `${currentRatio * 100}%`;
            this.pausedProgress = null;
            this.startVideoProgressLoop(video);
            return;
        }

        const currentRatio = Math.max(0, Math.min(1, this.pausedProgress.ratio));
        const remainingTime = Math.max(0, (1 - currentRatio) * this.slideDuration);

        if (this.progressBar) {
            this.progressBar.style.setProperty('--studio-progress-duration', `${remainingTime}ms`);
        }

        // Continue animation from current position to 100% for this slide
        this.progressFill.style.transition = 'none';
        this.progressFill.style.width = `${currentRatio * 100}%`;
        void this.progressFill.offsetWidth;
        this.progressFill.style.transition = `width ${remainingTime}ms linear`;
        this.progressFill.style.width = '100%';

        this.pausedProgress = null;

        this.progressTimer = setTimeout(() => {
            if (this.isPaused) return;
            this.goToNext();
        }, remainingTime);
    }

    updateProgressBar() {
        if (!this.progressFill) return;
        if (this.isPaused) return; // Don't auto-advance when paused

        if (this.progressTimer) {
            clearTimeout(this.progressTimer);
            this.progressTimer = null;
        }

        const total = Math.max(this.slides.length, 1);

        // Check if current slide is a video
        const currentSlide = this.slides[this.currentIndex];
        const video = this.getActiveSlideVideo(currentSlide);
        const isVideoSlide = !!video;

        if (this.progressBar) {
            this.progressBar.style.setProperty('--studio-progress-duration', `${this.slideDuration}ms`);
        }

        // Video slides: progress must reflect actual playback time.
        // Do NOT perform the duration-based 0% -> 100% animation here, because
        // video metadata (duration) may not be available immediately and would
        // leave the bar stuck at 100% until the loop can correct it.
        if (isVideoSlide) {
            this.progressFill.style.transition = 'none';
            this.progressFill.style.width = '0%';

            if (video) {
                this.startVideoProgressLoop(video);
            }
            return;
        }

        // Progress is per current slide: reset to 0% then animate to 100%
        this.progressFill.style.transition = 'none';
        this.progressFill.style.width = '0%';
        void this.progressFill.offsetWidth;
        this.progressFill.style.transition = `width ${this.slideDuration}ms linear`;
        this.progressFill.style.width = '100%';

        // Only auto-advance when there is more than one slide.
        if (total <= 1) return;

        this.progressTimer = setTimeout(() => {
            if (this.isPaused) return;
            this.goToNext();
        }, this.slideDuration);
    }

    destroy() {
        this.stopVideoProgressLoop();
        this.viewport.removeEventListener('keydown', this.handleKeydown);
        this.viewport.removeEventListener('mouseleave', this.handleLeave);

        if (this.hoverResumeListenerAttached && this.viewport) {
            this.viewport.removeEventListener('mousemove', this.handleHoverResume);
            this.hoverResumeListenerAttached = false;
        }

        if (this.leftHotspot) {
            this.leftHotspot.removeEventListener('mouseenter', this.handleHoverLeft);
            this.leftHotspot.removeEventListener('mouseleave', this.handleHoverClear);
            this.leftHotspot.removeEventListener('click', this.handleClickLeft);
        }

        if (this.rightHotspot) {
            this.rightHotspot.removeEventListener('mouseenter', this.handleHoverRight);
            this.rightHotspot.removeEventListener('mouseleave', this.handleHoverClear);
            this.rightHotspot.removeEventListener('click', this.handleClickRight);
        }

        if (this.progressTimer) {
            clearTimeout(this.progressTimer);
            this.progressTimer = null;
        }

        // Pause and clean up all videos
        this.slides.forEach(slide => {
            slide.querySelectorAll('[data-studio-video]').forEach((video) => {
                if (!video) return;
                video.pause();
                video.onended = null;
            });
        });
        this.currentVideo = null;

        if (this.breakpointMql && this.handleBreakpointChange) {
            if (typeof this.breakpointMql.removeEventListener === 'function') {
                this.breakpointMql.removeEventListener('change', this.handleBreakpointChange);
            } else if (typeof this.breakpointMql.removeListener === 'function') {
                this.breakpointMql.removeListener(this.handleBreakpointChange);
            }
        }

        this.root.__studioSlider = null;
    }
}

function initStudioSliders(scope = document, options = {}) {
    const sliders = Array.from(scope.querySelectorAll('[data-studio-slider]'));
    const allSliders = Array.from(document.querySelectorAll('[data-studio-slider]'));

    sliders.forEach((slider) => {
        if (slider.__studioSlider) return;
        // On initial page load, auto-start only the first slider on the page
        // For section reloads, let StudioPageSnap handle the play state
        const globalIndex = allSliders.indexOf(slider);
        const isFirst = globalIndex === 0 && scope === document;
        slider.__studioSlider = new StudioSlider(slider, { autoStart: isFirst && !options.sectionReload });
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initStudioSliders();
    initStudioPageSnap();
});

// When restoring from back/forward cache, ensure the progress bar doesn't show a stale 100% state.
window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-studio-slider]').forEach((sliderEl) => {
        const instance = sliderEl.__studioSlider;
        if (instance && typeof instance.initProgressBar === 'function') {
            instance.initProgressBar();
        } else {
            const fill = sliderEl.querySelector('.studio-slider__progress-fill');
            if (fill) {
                fill.style.transition = 'none';
                fill.style.width = '0%';
            }
        }
    });
});

// Fallback: if script loads after DOMContentLoaded, init immediately
if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initStudioSliders();
    initStudioPageSnap();
}

window.addEventListener('load', () => {
    initStudioSliders();
    initStudioPageSnap();
});

function supportsNativeProjectSnap() {
    try {
        return (
            typeof window.CSS !== 'undefined' &&
            typeof window.CSS.supports === 'function' &&
            (window.CSS.supports('scroll-snap-type: y mandatory') || window.CSS.supports('scroll-snap-type', 'y mandatory'))
        );
    } catch (_e) {
        return false;
    }
}

class StudioPageSnapNative {
    constructor() {
        this.projects = Array.from(document.querySelectorAll('[data-studio-project]'));
        this.enabled = this.projects.length > 0 && document.body.classList.contains('template-page-studio');
        this.activeIndex = 0;
        this.rafId = null;

        if (!this.enabled) return;

        document.documentElement.classList.add('studio-snap-native');

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });

        this.updateHeaderOffsetVar();
        this.activeIndex = this.getNearestIndex();
        this.updateActiveProjectState();
    }

    destroy() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        document.documentElement.classList.remove('studio-snap-native');
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    onResize() {
        this.updateHeaderOffsetVar();
        this.onScroll();
    }

    onScroll() {
        if (!this.enabled) return;
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            const nextIndex = this.getNearestIndex();
            if (nextIndex !== this.activeIndex) {
                this.activeIndex = nextIndex;
                this.updateActiveProjectState();
            }
        });
    }

    getHeaderOffset() {
        const header = document.querySelector('.header-wrapper');
        if (!header) return 0;
        const styles = window.getComputedStyle(header);
        const position = styles.position;
        if (position === 'fixed' || position === 'sticky') {
            return header.offsetHeight;
        }
        return 0;
    }

    updateHeaderOffsetVar() {
        const offset = this.getHeaderOffset();
        document.documentElement.style.setProperty('--studio-header-offset', `${offset}px`);
    }

    getNearestIndex() {
        const headerOffset = this.getHeaderOffset();
        const viewportMid = headerOffset + (window.innerHeight - headerOffset) / 2;
        let nearest = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.projects.forEach((project, index) => {
            const rect = project.getBoundingClientRect();
            const mid = rect.top + rect.height / 2;
            const distance = Math.abs(mid - viewportMid);
            if (distance < nearestDistance) {
                nearest = index;
                nearestDistance = distance;
            }
        });

        return nearest;
    }

    updateActiveProjectState() {
        if (!this.projects.length) return;

        this.projects.forEach((project, index) => {
            const isActive = index === this.activeIndex;
            project.classList.toggle('studio-slider--active', isActive);

            const slider = project.__studioSlider;
            if (slider) {
                if (isActive) {
                    slider.play();
                    try {
                        preloadStudioProjectMedia(project, index, this.projects);
                    } catch (_e) {
                        // no-op
                    }
                } else {
                    slider.pause();
                }
            }
        });
    }
}

function getStudioProjectSlideUrl(slideEl) {
    if (!slideEl) return '';
    return slideEl.dataset.videoPoster || slideEl.dataset.image || '';
}

function warmImage(url) {
    if (!url) return;
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
}

function preloadStudioProjectMedia(projectEl, projectIndex, allProjects) {
    const slider = projectEl?.__studioSlider;
    if (!slider || !slider.slides || slider.slides.length === 0) return;

    const total = slider.slides.length;
    const current = slider.currentIndex || 0;
    const prev = (current - 1 + total) % total;
    const next = (current + 1) % total;

    // Only hydrate the active slide for display.
    const activeVariant = slider.isMobileViewport() ? 'mobile' : 'desktop';
    slider.ensureSlideHydrated(slider.slides[current], { variant: activeVariant, forPlayback: true });

    // Preload prev/next slides (hydrate enough for quick navigation + preview video).
    [prev, next].forEach((idx) => {
        const slide = slider.slides[idx];
        // Desktop is used for preview background/video.
        slider.ensureSlideHydrated(slide, { variant: 'desktop', forPlayback: false });
        // Active variant is what the user will actually see when navigating.
        slider.ensureSlideHydrated(slide, { variant: activeVariant, forPlayback: false });
        warmImage(getStudioProjectSlideUrl(slide));
    });

    // Preload the first image of the next project (do not hydrate its slide media).
    const nextProject = Array.isArray(allProjects) ? allProjects[projectIndex + 1] : null;
    const nextSlider = nextProject?.__studioSlider;
    const nextFirstSlide = nextSlider?.slides?.[0] || null;
    warmImage(getStudioProjectSlideUrl(nextFirstSlide));
}

function initStudioPageSnap() {
    const projects = document.querySelectorAll('[data-studio-project]');
    if (projects.length === 0) {
        document.documentElement.classList.remove('studio-snap-native');
        return;
    }
    if (window.__studioPageSnap && typeof window.__studioPageSnap.destroy === 'function') {
        window.__studioPageSnap.destroy();
    }
    try {
        if (supportsNativeProjectSnap()) {
            window.__studioPageSnap = new StudioPageSnapNative();
        }
    } catch (e) {
        // Slider instance creation failed; skip this instance.
    }
}

document.addEventListener('shopify:section:load', (event) => {
    initStudioSliders(event.target, { sectionReload: true });
    initStudioPageSnap();
});

document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-studio-slider]').forEach((slider) => {
        if (slider.__studioSlider && typeof slider.__studioSlider.destroy === 'function') {
            slider.__studioSlider.destroy();
        }
    });
    initStudioPageSnap();
});

// Mutation observer to catch DOM changes that add/remove projects outside section events
const studioObserver = new MutationObserver(() => {
    initStudioPageSnap();
});

studioObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
});
